import { useEffect, useMemo, useState } from 'react'
import {
  fetchSeriesDetails,
  fetchSeriesCast,
  fetchSimilarSeries,
  fetchSeriesSeasons,
  fetchSeasonEpisodes,
  type TmdbSeriesDetails,
  type TmdbCastMember,
  type TmdbSimilarTitle,
  type TmdbSeason,
  type TmdbEpisode,
} from '../tmdb/tmdb'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import { useLibrarySeries } from '../library/useLibrary'
import { useWriteLibrary } from '../library/useWriteLibrary'
import { useAwardsIndex } from '../awards/useAwards'
import { awardsForMovie, type MovieAwardEntry } from '../awards/awards'
import { WatchlistPicker } from '../watchlists/WatchlistPicker'
import { LazyImage, useToast, useConfirm } from '../ui'
import { useBackButtonClose } from '../hooks/useBackButtonClose'
import type { CloudEpisodeState, CloudSeriesRecord } from '@govu/shared'
import { SeriesEpisodeList, episodeKey } from './SeriesEpisodeList'

interface Props {
  tmdbId: number | null
  onClose: () => void
  onPlayEpisode: (tmdbId: number, season: number, episode: number) => void
  onPlay: (tmdbId: number, type: 'movie' | 'tv') => void
  onPersonOpen: (personId: number) => void
  /**
   * Fires when the user clicks a citation in the compact Awards section.
   * LibraryView uses this to close the modal, switch to the Awards tab
   * and pre-select the matching body + ceremony.
   */
  onJumpToAwards?: (bodyWikidataId: string, editionId: string) => void
}

/**
 * Fullscreen series detail modal. Ported 1:1 from the desktop
 * `SeriesDetailView` + `EpisodeList`:
 *
 *  - Backdrop hero with title, year, season/episode counts, TMDB status
 *    (Returning / Ended / Canceled), genres, TMDB vote badge.
 *  - User-rating stars (gold) with a clear button — rating the show
 *    auto-adds it to the library like every other tap-to-add flow in
 *    the PWA.
 *  - Overview / synopsis.
 *  - Continue-watching banner when there's a `lastWatched` episode.
 *  - Action row: Watch now, random episode, watchlist, rewatch, …
 *  - Season picker (horizontal tab bar).
 *  - Rich two-pane `SeriesEpisodeList` with episode stills, runtimes,
 *    ratings, air dates, overviews, progress bars, mark-watched /
 *    mark-until-here / play actions, and a context menu.
 *  - Cast carousel with per-person click-through.
 *  - Awards section (reuses the PWA awards index — tmdbId is shared
 *    between movies and TV).
 *  - Recommended series grid.
 *
 * Season and episode data is loaded lazily — only the currently active
 * season fetches its episode list. `markWatchedUntil` walks over only
 * the seasons that the user has already peeked at, which matches the
 * desktop fetch-on-open UX.
 */
export function SeriesDetailModal({
  tmdbId,
  onClose,
  onPlayEpisode,
  onPlay,
  onPersonOpen,
  onJumpToAwards,
}: Props) {
  const { settings } = useUserSettings()
  // `libraryLoading` matters here: until the Firestore snapshot resolves
  // we MUST NOT treat "no entry" as "series isn't in library", or the
  // rating buttons fall into the add-to-library branch and overwrite a
  // real synced rating with a fresh empty record.
  const { entries: librarySeries, loading: libraryLoading } =
    useLibrarySeries()
  const {
    addSeriesToLibrary,
    setEpisodeWatched,
    updateSeries,
    markWatchedUntil,
  } = useWriteLibrary()
  const { index: awardsIndex } = useAwardsIndex()
  const { showSuccess, showError, showInfo } = useToast()
  const confirm = useConfirm()
  const [details, setDetails] = useState<TmdbSeriesDetails | null>(null)
  const [cast, setCast] = useState<TmdbCastMember[]>([])
  const [recommended, setRecommended] = useState<TmdbSimilarTitle[]>([])
  const [seasons, setSeasons] = useState<TmdbSeason[]>([])
  const [activeSeason, setActiveSeason] = useState<number | null>(null)
  const [episodesBySeason, setEpisodesBySeason] = useState<
    Map<number, TmdbEpisode[]>
  >(new Map())
  const [episodesLoading, setEpisodesLoading] = useState(false)
  const [writeBusy, setWriteBusy] = useState(false)
  const [watchlistOpen, setWatchlistOpen] = useState(false)

  // Back button closes the inner Watchlist picker before the series
  // detail modal. The detail modal's own entry is managed by the
  // library shell in LibraryView.
  useBackButtonClose(watchlistOpen, () => setWatchlistOpen(false))

  const cloudSeries = useMemo<CloudSeriesRecord | undefined>(
    () => librarySeries.find((s) => s.tmdbId === tmdbId)?.record,
    [librarySeries, tmdbId]
  )
  const inLibrary = !!cloudSeries
  // Defensive coercion mirrors SeriesCard: tolerate a string-shaped
  // rating surfaced from a Firestore doc rather than silently rendering
  // 0/10 in the stars.
  const userRating = (() => {
    const raw = cloudSeries?.userRating
    if (raw == null) return 0
    const num = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(num) && num > 0 ? num : 0
  })()

  const awards = useMemo<MovieAwardEntry[]>(() => {
    if (!awardsIndex || tmdbId == null) return []
    // The awards scraper stores by `movieTmdbId` but the same index holds
    // TV ids too — there's only one TMDB id namespace for our purposes.
    return awardsForMovie(awardsIndex, tmdbId)
  }, [awardsIndex, tmdbId])

  /**
   * Find the user's most-recently-watched episode across every season
   * based on `lastWatched` timestamps in the cloud record. Returns the
   * winning season + episode number so the banner and season picker
   * can jump there automatically.
   */
  const lastWatchedLookup = useMemo<{
    seasonNumber: number
    episodeNumber: number
    state: CloudEpisodeState
  } | null>(() => {
    if (!cloudSeries?.episodes) return null
    let best:
      | { seasonNumber: number; episodeNumber: number; state: CloudEpisodeState; ts: number }
      | null = null
    for (const [sStr, epsMap] of Object.entries(cloudSeries.episodes)) {
      const seasonNumber = Number(sStr)
      if (!Number.isFinite(seasonNumber)) continue
      for (const [eStr, state] of Object.entries(epsMap ?? {})) {
        const episodeNumber = Number(eStr)
        if (!Number.isFinite(episodeNumber)) continue
        const ts = state?.lastWatched ? Date.parse(state.lastWatched) : NaN
        if (!Number.isFinite(ts)) continue
        if (best == null || ts > best.ts) {
          best = { seasonNumber, episodeNumber, state, ts }
        }
      }
    }
    return best
      ? {
          seasonNumber: best.seasonNumber,
          episodeNumber: best.episodeNumber,
          state: best.state,
        }
      : null
  }, [cloudSeries?.episodes])

  const lastWatchedKey = lastWatchedLookup
    ? episodeKey(lastWatchedLookup.seasonNumber, lastWatchedLookup.episodeNumber)
    : null

  /**
   * Cloud accessor for the episode list — unwraps the nested
   * `episodes[s][e]` structure one lookup at a time so the episode
   * list component can stay shape-agnostic.
   */
  const lookupCloud = (
    seasonNumber: number,
    episodeNumber: number
  ): CloudEpisodeState | undefined => {
    return cloudSeries?.episodes?.[String(seasonNumber)]?.[String(episodeNumber)]
  }

  // We refuse to write while the library subscription is still loading —
  // otherwise we'd treat "snapshot not in yet" as "series not in library"
  // and clobber the user's existing synced rating with a fresh add.
  const handleSetRating = async (rating: number) => {
    if (tmdbId == null) return
    if (libraryLoading) {
      showInfo('Loading library — try again in a second')
      return
    }
    setWriteBusy(true)
    try {
      if (!inLibrary) await addSeriesToLibrary(tmdbId)
      await updateSeries(tmdbId, { userRating: rating })
      showSuccess(`Rated ${rating}/10`)
    } catch (err) {
      console.error('[SeriesDetailModal] set rating failed', err)
      showError('Failed to save rating')
    } finally {
      setWriteBusy(false)
    }
  }

  const handleClearRating = async () => {
    if (tmdbId == null) return
    if (libraryLoading) {
      showInfo('Loading library — try again in a second')
      return
    }
    setWriteBusy(true)
    try {
      await updateSeries(tmdbId, { userRating: null })
      showInfo('Rating cleared')
    } catch (err) {
      console.error('[SeriesDetailModal] clear rating failed', err)
      showError('Failed to clear rating')
    } finally {
      setWriteBusy(false)
    }
  }

  const handleToggleEpisodeWatched = async (
    episode: TmdbEpisode,
    nextWatched: boolean
  ) => {
    if (tmdbId == null) return
    setWriteBusy(true)
    try {
      if (!inLibrary) await addSeriesToLibrary(tmdbId)
      await setEpisodeWatched(
        tmdbId,
        episode.seasonNumber,
        episode.episodeNumber,
        nextWatched
      )
      showInfo(
        nextWatched
          ? `Marked S${episode.seasonNumber}E${episode.episodeNumber} watched`
          : `Unmarked S${episode.seasonNumber}E${episode.episodeNumber}`
      )
    } catch (err) {
      console.error('[SeriesDetailModal] toggle watched failed', err)
      showError('Failed to update episode')
    } finally {
      setWriteBusy(false)
    }
  }

  const handleMarkWatchedUntil = async (episode: TmdbEpisode) => {
    if (tmdbId == null) return
    const ok = await confirm({
      title: 'Mark watched until here?',
      message: `Mark every episode up to S${episode.seasonNumber}E${episode.episodeNumber} as watched?`,
      confirmLabel: 'Mark watched',
    })
    if (!ok) return
    setWriteBusy(true)
    try {
      if (!inLibrary) await addSeriesToLibrary(tmdbId)
      // Build the seasons payload from what we've loaded. Seasons not
      // yet fetched get skipped — by the time the user invokes "until
      // here" they've almost certainly already opened every earlier
      // season, matching the desktop fetch-on-open UX.
      const payload: { seasonNumber: number; episodeNumbers: number[] }[] = []
      for (const season of seasons) {
        const eps = episodesBySeason.get(season.seasonNumber)
        if (!eps) continue
        payload.push({
          seasonNumber: season.seasonNumber,
          episodeNumbers: eps.map((e) => e.episodeNumber),
        })
      }
      // Count episodes up to and including the target episode so the
      // toast reflects what the user will see change.
      let total = 0
      for (const entry of payload) {
        if (entry.seasonNumber < episode.seasonNumber) {
          total += entry.episodeNumbers.length
        } else if (entry.seasonNumber === episode.seasonNumber) {
          total += entry.episodeNumbers.filter(
            (n) => n <= episode.episodeNumber
          ).length
        }
      }
      await markWatchedUntil(
        tmdbId,
        payload,
        episode.seasonNumber,
        episode.episodeNumber
      )
      showSuccess(`Marked ${total} episode${total === 1 ? '' : 's'}`)
    } catch (err) {
      console.error('[SeriesDetailModal] mark watched until failed', err)
      showError('Failed to mark episodes')
    } finally {
      setWriteBusy(false)
    }
  }

  const handlePlayEpisode = (episode: TmdbEpisode) => {
    if (tmdbId == null) return
    onPlayEpisode(tmdbId, episode.seasonNumber, episode.episodeNumber)
  }

  /**
   * Random-episode action (desktop parity). Picks uniformly from every
   * episode we've already fetched across every season. Falls back to
   * the currently active season's list when the user hasn't peeked at
   * any other seasons yet.
   */
  const handleRandomEpisode = () => {
    if (tmdbId == null) return
    const pool: TmdbEpisode[] = []
    for (const eps of episodesBySeason.values()) {
      for (const e of eps) pool.push(e)
    }
    if (pool.length === 0) return
    const pick = pool[Math.floor(Math.random() * pool.length)]
    onPlayEpisode(tmdbId, pick.seasonNumber, pick.episodeNumber)
  }

  /**
   * Continue-watching click: jump into the single most-recently-watched
   * episode (or, if it's finished, suggest the next episode after it).
   */
  const handleContinue = () => {
    if (tmdbId == null || !lastWatchedLookup) return
    let season = lastWatchedLookup.seasonNumber
    let episode = lastWatchedLookup.episodeNumber
    const finished = !!lastWatchedLookup.state.isWatched
    if (finished) {
      const currentSeasonEps = episodesBySeason.get(season)
      if (currentSeasonEps) {
        const next = currentSeasonEps.find((e) => e.episodeNumber > episode)
        if (next) {
          episode = next.episodeNumber
        } else {
          // Find next regular season (skip specials).
          const nextSeason = seasons.find(
            (s) => s.seasonNumber > season && s.seasonNumber > 0
          )
          if (nextSeason) {
            season = nextSeason.seasonNumber
            episode = 1
          }
        }
      }
    }
    onPlayEpisode(tmdbId, season, episode)
  }

  /**
   * "Start Watching" CTA — fires when the user has never watched a
   * single episode of this series. Picks the first episode of the
   * lowest non-zero season (typically S1E1) and never starts on
   * Specials.
   */
  const handleStartWatching = () => {
    if (tmdbId == null) return
    const firstRegular =
      seasons.find((s) => s.seasonNumber > 0) ?? seasons[0]
    if (!firstRegular) return
    onPlayEpisode(tmdbId, firstRegular.seasonNumber, 1)
  }

  useEffect(() => {
    if (tmdbId == null) {
      setDetails(null)
      setCast([])
      setRecommended([])
      setSeasons([])
      setActiveSeason(null)
      setEpisodesBySeason(new Map())
      return
    }
    // Falls back to the bundled developer key when the user hasn't supplied one.
    const apiKey = getEffectiveTmdbApiKey(settings)
    if (!apiKey) return
    const language = settings?.tmdbPreferredLanguage || 'en'

    let cancelled = false
    setDetails(null)
    setCast([])
    setRecommended([])

    fetchSeriesDetails(tmdbId, apiKey, language).then((d) => {
      if (!cancelled) setDetails(d)
    })
    fetchSeriesCast(tmdbId, apiKey, language).then((c) => {
      if (!cancelled) setCast(c)
    })
    fetchSimilarSeries(tmdbId, apiKey, language).then((s) => {
      if (!cancelled) setRecommended(s)
    })
    fetchSeriesSeasons(tmdbId, apiKey, language).then((s) => {
      if (cancelled) return
      // Sort regular seasons first, then specials (season 0) at the end —
      // matches the desktop SeriesDetailView ordering so the season picker
      // never opens with "Specials" pre-selected.
      const sorted = [...s].sort((a, b) => {
        const aSpecial = a.seasonNumber === 0
        const bSpecial = b.seasonNumber === 0
        if (aSpecial !== bSpecial) return aSpecial ? 1 : -1
        return a.seasonNumber - b.seasonNumber
      })
      setSeasons(sorted)
      // Default to the lowest non-zero season (Season 1 in nearly every
      // show). Never default to specials. The continue-watching position
      // takes priority — but only if it's a regular season too.
      let pick: TmdbSeason | undefined =
        sorted.find((season) => season.seasonNumber > 0) || sorted[0]
      if (lastWatchedLookup && lastWatchedLookup.seasonNumber > 0) {
        const targetSeason = sorted.find(
          (season) => season.seasonNumber === lastWatchedLookup.seasonNumber
        )
        if (targetSeason) {
          const finale =
            lastWatchedLookup.episodeNumber >= targetSeason.episodeCount &&
            !!lastWatchedLookup.state.isWatched
          if (finale) {
            const next = sorted.find(
              (season) =>
                season.seasonNumber === lastWatchedLookup.seasonNumber + 1 &&
                season.seasonNumber > 0
            )
            pick = next ?? targetSeason
          } else {
            pick = targetSeason
          }
        }
      }
      if (pick) setActiveSeason(pick.seasonNumber)
    })

    return () => {
      cancelled = true
    }
    // lastWatchedLookup is intentionally omitted: we only want the
    // auto-select to run on first open, not every time cloud state
    // updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tmdbId, settings?.tmdbApiKey, settings?.tmdbPreferredLanguage])

  useEffect(() => {
    if (tmdbId == null || activeSeason == null) return
    if (episodesBySeason.has(activeSeason)) return

    // Falls back to the bundled developer key when the user hasn't supplied one.
    const apiKey = getEffectiveTmdbApiKey(settings)
    if (!apiKey) return
    const language = settings?.tmdbPreferredLanguage || 'en'

    let cancelled = false
    setEpisodesLoading(true)
    fetchSeasonEpisodes(tmdbId, activeSeason, apiKey, language).then((eps) => {
      if (cancelled) return
      setEpisodesBySeason((prev) => {
        const next = new Map(prev)
        next.set(activeSeason, eps)
        return next
      })
      setEpisodesLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [
    tmdbId,
    activeSeason,
    settings?.tmdbApiKey,
    settings?.tmdbPreferredLanguage,
    episodesBySeason,
  ])

  useEffect(() => {
    if (tmdbId == null) return
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [tmdbId, onClose])

  if (tmdbId == null) return null

  const activeEpisodes =
    activeSeason != null ? episodesBySeason.get(activeSeason) ?? null : null
  const activeSeasonObj =
    activeSeason != null
      ? seasons.find((s) => s.seasonNumber === activeSeason) ?? null
      : null
  const activeSeasonIndex =
    activeSeason != null
      ? seasons.findIndex((s) => s.seasonNumber === activeSeason)
      : -1

  // Resolve the episode name associated with `lastWatchedLookup` so the
  // continue-watching banner can show "S2E3 · The One Where …" when the
  // season data is already loaded.
  const lastWatchedEpisodeName =
    lastWatchedLookup && episodesBySeason.has(lastWatchedLookup.seasonNumber)
      ? episodesBySeason
          .get(lastWatchedLookup.seasonNumber)!
          .find((e) => e.episodeNumber === lastWatchedLookup.episodeNumber)?.name ?? null
      : null

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-bg-primary font-inter">
      <div className="relative">
        {details?.backdropUrl && (
          <div className="relative h-56 w-full sm:h-72 md:h-[28rem]">
            <LazyImage
              src={details.backdropUrl}
              alt=""
              className="h-full w-full"
              placeholderColor="#1a1a1a"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/60 to-bg-primary/10" />
            <div className="absolute inset-0 bg-gradient-to-r from-bg-primary/60 via-transparent to-transparent" />
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="fixed right-4 top-4 z-10 rounded-full bg-bg-secondary/80 p-2 text-text-primary shadow-cinematic backdrop-blur transition-colors hover:bg-bg-tertiary"
        >
          <CloseIcon />
        </button>

        <div className="relative mx-auto -mt-28 max-w-screen-2xl px-4 pb-12 sm:-mt-32 md:-mt-40">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
            {details?.posterUrl ? (
              <LazyImage
                src={details.posterUrl}
                alt={details.name}
                wrapperClassName="h-56 w-36 shrink-0 rounded-lg border border-border-primary shadow-cinematic-lg sm:h-72 sm:w-48"
                placeholderColor="#1a1a1a"
              />
            ) : (
              <div className="h-56 w-36 shrink-0 rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary sm:h-72 sm:w-48" />
            )}
            <div className="min-w-0 flex-1 pt-2">
              <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-3xl md:text-4xl">
                {details?.name || `TMDB #${tmdbId}`}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                {details?.year && (
                  <span className="font-semibold text-accent-gold">
                    {details.year}
                  </span>
                )}
                <span className="rounded border border-purple-500/30 bg-purple-500/15 px-1.5 py-0.5 text-purple-300">
                  TV Series
                </span>
                {details && details.numberOfSeasons > 0 && (
                  <span className="text-text-secondary">
                    {details.numberOfSeasons} season
                    {details.numberOfSeasons === 1 ? '' : 's'}
                  </span>
                )}
                {details && details.numberOfEpisodes > 0 && (
                  <span className="text-text-secondary">
                    {details.numberOfEpisodes} episodes
                  </span>
                )}
                {details && details.rating > 0 && (
                  <span className="flex items-center gap-1 rounded bg-blue-500/15 px-1.5 py-0.5 text-blue-300">
                    ★ {details.rating.toFixed(1)}
                  </span>
                )}
                {details?.status && (
                  <span className="text-text-tertiary">{details.status}</span>
                )}
                {details?.genres.slice(0, 3).map((g) => (
                  <span
                    key={g}
                    className="rounded bg-bg-secondary px-1.5 py-0.5 text-text-secondary"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {/* Primary CTA — flips between "Start Watching" and
                    "Continue Watching: S{n}E{m}" depending on whether
                    the user has any progress on this series. Always
                    plays a specific episode (S1E1 by default) instead
                    of the generic series-level Watch action. */}
                {lastWatchedLookup ? (
                  <button
                    type="button"
                    onClick={handleContinue}
                    className="inline-flex items-center gap-2 rounded-md bg-accent-primary px-5 py-2.5 text-sm font-semibold text-black shadow-cinematic transition-colors hover:bg-accent-hover"
                  >
                    <PlayIcon />
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-[9px] uppercase tracking-wider opacity-75">
                        Continue Watching
                      </span>
                      <span>
                        S{lastWatchedLookup.seasonNumber}E
                        {lastWatchedLookup.episodeNumber}
                      </span>
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartWatching}
                    disabled={seasons.length === 0}
                    className="inline-flex items-center gap-2 rounded-md bg-accent-primary px-5 py-2.5 text-sm font-semibold text-black shadow-cinematic transition-colors hover:bg-accent-hover disabled:opacity-50"
                  >
                    <PlayIcon /> Start Watching
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onPlay(tmdbId, 'tv')}
                  className="inline-flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-bg-tertiary"
                  title="Play series via streaming source"
                >
                  <PlayIcon /> Stream
                </button>
                <button
                  type="button"
                  onClick={handleRandomEpisode}
                  disabled={episodesBySeason.size === 0}
                  className="inline-flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-bg-tertiary disabled:opacity-50"
                  title="Play a random episode from the seasons you've opened"
                >
                  <DiceIcon /> Random
                </button>
                {details && (
                  <button
                    type="button"
                    onClick={() => setWatchlistOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-bg-tertiary"
                  >
                    ☆ Watchlist
                  </button>
                )}
              </div>

              <SeriesRatingStars
                value={userRating}
                onChange={handleSetRating}
                onClear={handleClearRating}
                disabled={writeBusy || libraryLoading}
              />
              {details && (
                <p className="mt-1 text-[11px] text-text-tertiary">
                  {libraryLoading
                    ? 'Loading your rating…'
                    : inLibrary
                      ? '✓ In library'
                      : 'Tap a star to add to your library'}
                </p>
              )}
            </div>
          </div>

          {details?.overview && (
            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-text-secondary">
              {details.overview}
            </p>
          )}

          {lastWatchedLookup && (
            <button
              type="button"
              onClick={handleContinue}
              className="mt-6 flex w-full items-center gap-3 rounded-lg border border-accent-primary/30 bg-accent-primary/5 px-4 py-3 text-left transition-colors hover:bg-accent-primary/10"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-primary text-black">
                <PlayIcon />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-accent-primary">
                  Continue watching
                </span>
                <span className="mt-0.5 block truncate text-sm font-medium text-text-primary">
                  S{lastWatchedLookup.seasonNumber}E
                  {lastWatchedLookup.episodeNumber}
                  {lastWatchedEpisodeName ? ` · ${lastWatchedEpisodeName}` : ''}
                </span>
              </span>
            </button>
          )}

          <Section title="Episodes">
            {seasons.length === 0 ? (
              <p className="py-8 text-center text-xs text-text-tertiary">
                Loading seasons…
              </p>
            ) : (
              <>
                <div className="-mx-4 mb-4 flex items-center gap-2 overflow-x-auto px-4 scrollbar-hide">
                  {seasons.map((s, idx) => {
                    // Insert a divider just before the first specials
                    // entry so they read as a separate group at the end.
                    const showDivider =
                      s.seasonNumber === 0 &&
                      idx > 0 &&
                      seasons[idx - 1].seasonNumber !== 0
                    return (
                      <div key={s.seasonNumber} className="flex shrink-0 items-center gap-2">
                        {showDivider && (
                          <span
                            aria-hidden
                            className="h-5 w-px shrink-0 bg-border-primary"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => setActiveSeason(s.seasonNumber)}
                          className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            activeSeason === s.seasonNumber
                              ? 'bg-accent-primary text-black'
                              : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                          } ${
                            s.seasonNumber === 0 && activeSeason !== s.seasonNumber
                              ? 'italic text-text-tertiary'
                              : ''
                          }`}
                        >
                          {s.name}
                        </button>
                      </div>
                    )
                  })}
                </div>

                <SeriesEpisodeList
                  season={activeSeasonObj}
                  episodes={activeEpisodes}
                  loading={episodesLoading}
                  lookupCloud={lookupCloud}
                  seriesTmdbId={tmdbId}
                  lastWatchedEpisodeKey={lastWatchedKey}
                  onPlay={handlePlayEpisode}
                  onToggleWatched={handleToggleEpisodeWatched}
                  onMarkWatchedUntil={handleMarkWatchedUntil}
                  hasPrevSeason={activeSeasonIndex > 0}
                  hasNextSeason={
                    activeSeasonIndex >= 0 && activeSeasonIndex < seasons.length - 1
                  }
                  onPrevSeason={() => {
                    if (activeSeasonIndex > 0) {
                      setActiveSeason(seasons[activeSeasonIndex - 1].seasonNumber)
                    }
                  }}
                  onNextSeason={() => {
                    if (
                      activeSeasonIndex >= 0 &&
                      activeSeasonIndex < seasons.length - 1
                    ) {
                      setActiveSeason(seasons[activeSeasonIndex + 1].seasonNumber)
                    }
                  }}
                  writeBusy={writeBusy}
                />
              </>
            )}
          </Section>

          <Section title="Cast">
            {cast.length === 0 && !details && (
              <p className="text-xs text-text-tertiary">Loading cast…</p>
            )}
            {cast.length === 0 && details && (
              <p className="text-xs text-text-tertiary">
                No cast information from TMDB.
              </p>
            )}
            {cast.length > 0 && (
              <div className="-mx-4 overflow-x-auto scrollbar-hide">
                <ul className="flex gap-3 px-4">
                  {cast.map((c) => (
                    <li key={c.tmdbId} className="w-24 shrink-0">
                      <button
                        type="button"
                        onClick={() => onPersonOpen(c.tmdbId)}
                        className="group block w-full text-left"
                      >
                        {c.profileUrl ? (
                          <LazyImage
                            src={c.profileUrl}
                            alt={c.name}
                            wrapperClassName="aspect-square w-full rounded-full border border-border-primary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-105"
                            placeholderColor="#1a1a1a"
                          />
                        ) : (
                          <div className="aspect-square w-full rounded-full border border-border-primary bg-bg-tertiary" />
                        )}
                        <p className="mt-1.5 line-clamp-1 text-xs font-medium text-text-primary">
                          {c.name}
                        </p>
                        {c.character && (
                          <p className="line-clamp-1 text-[10px] text-text-tertiary">
                            {c.character}
                          </p>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* Awards — compact list with deep-link to the Awards tab. */}
          {(awardsIndex === null || awards.length > 0) && (
            <CompactAwardsSection
              awards={awards}
              loading={!awardsIndex}
              onJumpToAwards={onJumpToAwards}
            />
          )}

          {details && (
            <WatchlistPicker
              open={watchlistOpen}
              onClose={() => setWatchlistOpen(false)}
              movie={{
                tmdbId,
                title: details.name,
                year: details.year,
                posterPath: tmdbRelativePath(details.posterUrl),
              }}
            />
          )}

          {recommended.length > 0 && (
            <Section title="Recommended series">
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-8">
                {recommended.map((s) => (
                  <SimilarTile key={s.tmdbId} title={s} onPlay={onPlay} />
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  )
}

function SimilarTile({
  title,
  onPlay,
}: {
  title: TmdbSimilarTitle
  onPlay: (tmdbId: number, type: 'movie' | 'tv') => void
}) {
  return (
    <button
      type="button"
      onClick={() => onPlay(title.tmdbId, title.mediaType)}
      className="group flex flex-col text-left"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-[1.03]">
        {title.posterUrl ? (
          <LazyImage
            src={title.posterUrl}
            alt={title.title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
            fallback={
              <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
                {title.title}
              </div>
            }
          />
        ) : (
          <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
            {title.title}
          </div>
        )}
      </div>
      <p className="mt-1.5 line-clamp-2 text-xs font-medium text-text-primary">
        {title.title}
      </p>
      {title.year != null && (
        <p className="text-[10px] font-medium text-accent-gold">{title.year}</p>
      )}
    </button>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 text-base font-semibold tracking-tight text-text-primary">
        {title}
      </h2>
      {children}
    </section>
  )
}

/**
 * Compact "Awards" section — parity with `MovieDetailModal`'s version.
 * A one-line aggregate header (`3 wins · 8 nominations`) followed by a
 * vertical stack of clickable citations. Clicking a citation fires
 * `onJumpToAwards` so LibraryView can close the modal, switch to the
 * Awards tab and pre-select the matching ceremony.
 */
function CompactAwardsSection({
  awards,
  loading,
  onJumpToAwards,
}: {
  awards: MovieAwardEntry[]
  loading: boolean
  onJumpToAwards?: (bodyWikidataId: string, editionId: string) => void
}) {
  const wins = awards.filter((a) => a.isWinner).length
  const noms = awards.length - wins
  const summary = useMemo(() => {
    if (awards.length === 0) return null
    const parts: string[] = []
    if (wins > 0) parts.push(`${wins} ${wins === 1 ? 'win' : 'wins'}`)
    if (noms > 0) parts.push(`${noms} ${noms === 1 ? 'nomination' : 'nominations'}`)
    return parts.join(' · ')
  }, [awards.length, wins, noms])

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="text-base font-semibold tracking-tight text-text-primary">
          Awards
        </h2>
        {summary && (
          <span className="text-xs font-medium text-text-tertiary">
            {summary}
          </span>
        )}
      </div>

      {loading && (
        <p className="text-xs text-text-tertiary">Loading awards…</p>
      )}

      {!loading && awards.length === 0 && (
        <p className="text-xs text-text-tertiary">
          No award nominations on file for this series.
        </p>
      )}

      {awards.length > 0 && (
        <ul className="space-y-1">
          {awards.map((a, i) => {
            const clickable = !!onJumpToAwards
            const prefix = a.isWinner ? '🏆' : 'Nominee ·'
            const prefixClass = a.isWinner
              ? 'text-accent-gold'
              : 'text-text-tertiary'
            return (
              <li key={`${a.bodyWikidataId}:${a.editionId}:${a.categoryName}:${i}`}>
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() =>
                    onJumpToAwards?.(a.bodyWikidataId, a.editionId)
                  }
                  className={`w-full rounded px-2 py-1 text-left text-xs leading-relaxed transition-colors ${
                    clickable
                      ? 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                      : 'cursor-default text-text-secondary'
                  }`}
                >
                  <span className={`mr-1.5 font-semibold ${prefixClass}`}>
                    {prefix}
                  </span>
                  <span className="font-medium text-text-primary">
                    {a.bodyName}
                  </span>
                  <span className="text-text-tertiary"> · </span>
                  <span>{a.categoryName}</span>
                  <span className="text-text-tertiary"> · </span>
                  <span className="tabular-nums text-text-tertiary">
                    {a.year}
                  </span>
                  {a.personName && (
                    <span className="text-text-tertiary">
                      {' '}
                      ({a.personName})
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function SeriesRatingStars({
  value,
  onChange,
  onClear,
  disabled,
}: {
  value: number
  onChange: (v: number) => void
  onClear: () => void
  disabled: boolean
}) {
  return (
    <div className="mt-4 flex items-center gap-1">
      <span className="mr-2 text-[11px] uppercase tracking-wider text-text-tertiary">
        Your rating
      </span>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
        const active = n <= value
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n === value ? 0 : n)}
            className={`text-xl leading-none transition-colors ${
              active ? 'text-accent-gold' : 'text-text-tertiary hover:text-text-secondary'
            } disabled:opacity-50`}
            aria-label={`Rate ${n}/10`}
          >
            ★
          </button>
        )
      })}
      {value > 0 && (
        <>
          <span className="ml-2 text-xs font-medium text-accent-gold">{value}/10</span>
          <button
            type="button"
            disabled={disabled}
            onClick={onClear}
            className="ml-2 text-[11px] text-text-tertiary hover:text-text-primary disabled:opacity-50"
          >
            Clear
          </button>
        </>
      )}
    </div>
  )
}

function tmdbRelativePath(url: string | null | undefined): string | null {
  if (!url) return null
  const m = url.match(/\/t\/p\/w\d+(\/.+)$/)
  return m ? m[1] : null
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  )
}

function DiceIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8.5" cy="8.5" r="1" fill="currentColor" />
      <circle cx="15.5" cy="15.5" r="1" fill="currentColor" />
      <circle cx="15.5" cy="8.5" r="1" fill="currentColor" />
      <circle cx="8.5" cy="15.5" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
